import { useEffect, useMemo, useState } from "preact/hooks";
import { fetchClients } from "../api";
import { AppShell } from "../components/AppShell";
import type { ClientsData, ClientSummaryRow } from "../types";
import { fmtDateTime, fmtDisplayName, fmtPhoneDisplay } from "../format";
import { AlertIconBadge, Badge } from "../components/atoms/Badge";
import { Pagination, usePagination } from "../components/Pagination";
import { buildEgoUrl } from "../components/CrmLinks";
import { useDebouncedSearch } from "../hooks/useDebouncedSearch";

type ClientSort = "last_seen_desc" | "first_seen_desc" | "name_asc" | "conversations_desc";

interface ClientFilters {
  q: string;
  onlyWithEntity: boolean;
}

const EMPTY_FILTERS: ClientFilters = {
  q: "",
  onlyWithEntity: false,
};

function passClientFilters(client: ClientSummaryRow, filters: ClientFilters): boolean {
  if (filters.onlyWithEntity && !client.ego_entity_id) return false;
  const q = filters.q.trim().toLowerCase();
  if (!q) return true;
  const haystack = [
    client.name,
    client.phone,
    fmtPhoneDisplay(client.phone),
  ].filter(Boolean).join(" ").toLowerCase();
  return haystack.includes(q);
}

function dateValue(value?: string | null): number {
  const n = new Date(value || 0).getTime();
  return Number.isFinite(n) ? n : 0;
}

function clientNameValue(client: ClientSummaryRow): string {
  const name = fmtDisplayName(client.name, "");
  return (name || client.phone || client.id || "").trim();
}

function sortClients(clients: ClientSummaryRow[], sort: ClientSort): ClientSummaryRow[] {
  return [...clients].sort((a, b) => {
    if (sort === "first_seen_desc") return dateValue(b.first_seen) - dateValue(a.first_seen);
    if (sort === "name_asc") {
      return clientNameValue(a).localeCompare(clientNameValue(b), "pt-BR", {
        sensitivity: "base",
        numeric: true,
      });
    }
    if (sort === "conversations_desc") {
      return b.conversations - a.conversations || dateValue(b.last_seen) - dateValue(a.last_seen);
    }
    return dateValue(b.last_seen) - dateValue(a.last_seen);
  });
}

export function ClientIndexPage({ mock }: { mock: boolean }) {
  const [data, setData] = useState<ClientsData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState<ClientFilters>(EMPTY_FILTERS);
  const { searchValue, setSearchValue, debouncedQuery } = useDebouncedSearch(filters.q);
  const [sort, setSort] = useState<ClientSort>("last_seen_desc");
  const [page, setPage] = useState(1);
  const suffix = mock ? "?mock=1" : "";
  const pageSize = 20;
  const effectiveFilters = useMemo<ClientFilters>(() => ({
    ...filters,
    q: debouncedQuery,
  }), [filters, debouncedQuery]);
  const filterKey = `${JSON.stringify(effectiveFilters)}:${sort}`;

  useEffect(() => {
    setPage(1);
  }, [filterKey]);

  useEffect(() => {
    setLoading(true);
    setError(null);
    fetchClients(mock, mock ? {} : {
      q: effectiveFilters.q,
      only_with_entity: effectiveFilters.onlyWithEntity,
      sort,
      page,
      page_size: pageSize,
    })
      .then((d) => {
        if (!d || d.ok === false) setError(d?.error || "Clientes indisponíveis.");
        else setData(d);
      })
      .catch((err: unknown) => setError(err instanceof Error ? err.message : String(err)))
      .finally(() => setLoading(false));
  }, [mock, filterKey, page]);

  const allClients = useMemo(() => data?.clients || [], [data]);
  const filteredClients = useMemo(
    () => mock ? allClients.filter((client) => passClientFilters(client, effectiveFilters)) : allClients,
    [allClients, filterKey, mock],
  );
  const clients = useMemo(
    () => mock ? sortClients(filteredClients, sort) : filteredClients,
    [filteredClients, sort, mock],
  );
  const localPagination = usePagination(clients, {
    pageSize,
    resetKey: filterKey,
  });
  const pageItems = mock ? localPagination.pageItems : clients;
  const serverTotal = data?.pagination?.total_items ?? clients.length;
  const serverPageCount = Math.max(1, data?.pagination?.page_count ?? 1);
  const serverRangeStart = serverTotal ? (page - 1) * pageSize + 1 : 0;
  const serverRangeEnd = Math.min(serverTotal, (page - 1) * pageSize + clients.length);

  const filtersHeader = data && !error
    ? (
      <section class="filter-bar filter-bar--clients filter-bar--header">
        <label class="field field--search">
          <span>Busca</span>
          <input
            value={searchValue}
            onInput={(e) => setSearchValue(e.currentTarget.value)}
            placeholder="Nome ou telefone"
          />
        </label>
        <label class="field">
          <span>Ordenar por</span>
          <select
            value={sort}
            onChange={(e) => setSort(e.currentTarget.value as ClientSort)}
          >
            <option value="last_seen_desc">Conversa mais recente</option>
            <option value="first_seen_desc">Cliente criado recentemente</option>
            <option value="name_asc">Nome A-Z</option>
            <option value="conversations_desc">Mais conversas</option>
          </select>
        </label>
        <label class="field field--checkbox">
          <input
            type="checkbox"
            checked={filters.onlyWithEntity}
            onChange={(e) => setFilters({ ...filters, onlyWithEntity: e.currentTarget.checked })}
          />
          <span>Somente com Entity eGO</span>
        </label>
      </section>
    )
    : null;

  return (
    <AppShell
      active="clientes"
      title="Clientes"
      subtitle={`${serverTotal} contatos`}
      mock={mock}
      headerExtra={filtersHeader}
    >
      {loading && !data && <div class="state-msg">Carregando clientes…</div>}
      {error && !data && <div class="state-error">{error}</div>}
      {data && !error && (
        <>
          <section class="rows-panel">
            <div class="rows-panel-head">
              <span>{clients.length} clientes</span>
              {mock && <Badge variant="info">mock</Badge>}
            </div>
            <div class="rows-panel-scroll">
              {loading && <div class="state-msg">Atualizando clientes…</div>}
              {pageItems.map((client) => (
                <article class="client-row" key={client.id}>
                  <div>
                    <div class="conv-row-title">
                      <a href={`/clientes/${client.id}${suffix}`}>{fmtDisplayName(client.name)}</a>
                      <span>{fmtPhoneDisplay(client.phone)}</span>
                    </div>
                    <div class="conv-row-meta">
                      <span>{client.email}</span>
                      <span>{client.conversations} conversas</span>
                      <span>Criado {fmtDateTime(client.first_seen)}</span>
                      <span>Última atividade {fmtDateTime(client.last_seen)}</span>
                    </div>
                  </div>
                  <div class="conv-row-badges">
                    <AlertIconBadge count={client.alerts} />
                  </div>
                  <div class="conv-row-actions">
                    {(() => {
                      const egoUrl = buildEgoUrl("person", client.ego_entity_id);
                      return egoUrl
                        ? (
                          <a class="btn" href={egoUrl} target="_blank" rel="noopener">
                            Entity eGO
                          </a>
                        )
                        : null;
                    })()}
                    <a class="btn" href={`/clientes/${client.id}${suffix}`}>Abrir</a>
                  </div>
                </article>
              ))}
            </div>
            <Pagination
              page={mock ? localPagination.page : page}
              pageCount={mock ? localPagination.pageCount : serverPageCount}
              rangeStart={mock ? localPagination.rangeStart : serverRangeStart}
              rangeEnd={mock ? localPagination.rangeEnd : serverRangeEnd}
              totalItems={mock ? localPagination.totalItems : serverTotal}
              onPageChange={mock ? localPagination.setPage : setPage}
            />
          </section>
        </>
      )}
    </AppShell>
  );
}
